import styled, { keyframes, css } from 'styled-components';
import { colors, radius, spacing } from '@ardoq/design-tokens';

const AI_GRADIENT = `linear-gradient(270deg, ${colors.brand100} 0%, ${colors.orange100} 100%)`;

const pulse = keyframes`
  0%, 100% {
    background-color: ${colors.surfaceDefaultSubtle};
  }
  50% {
    background-color: ${colors.grey200};
  }
`;

const pulseAI = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
`;

const SkeletonElement = styled.div<{
  $delay?: number;
  $width?: string;
  $height?: string;
  $borderRadius?: string;
  $variant: 'default' | 'ai';
}>`
  width: ${({ $width = '100%' }) => $width};
  height: ${({ $height = spacing.s16 }) => $height};
  border-radius: ${({ $borderRadius = radius.r4 }) => $borderRadius};
  ${({ $variant }) =>
    $variant === 'default'
      ? css`
          background: ${colors.surfaceDefaultSubtle};
          animation: ${pulse} 1.2s ease-in-out infinite;
        `
      : css`
          background: ${AI_GRADIENT};
          animation: ${pulseAI} 1.2s ease-in-out infinite;
        `};
  animation-delay: ${({ $delay = 0 }) => $delay}s;
`;

export type SkeletonProps = {
  /** Width of the skeleton. Defaults to '100%' */
  width?: string;
  /** Height of the skeleton. Defaults to spacing.s16 (16px) */
  height?: string;
  /** Border radius of the skeleton. Defaults to radius.r4 (4px) */
  borderRadius?: string;
  /** Animation delay in seconds. Useful for staggered loading effects */
  delay?: number;
  /** Variant of the skeleton shown. When gradient the skeleton design will pulse with opacity*/
  variant?: 'default' | 'ai';
  className?: string;
};

/**
 * A skeleton loading component that displays a pulsing placeholder
 * while content is loading. Use this to improve perceived performance
 * and provide visual feedback during data loading.
 */
export const Skeleton = ({
  width,
  height,
  borderRadius,
  delay,
  variant,
  className,
}: SkeletonProps) => (
  <SkeletonElement
    className={className}
    $width={width}
    $height={height}
    $borderRadius={borderRadius}
    $delay={delay}
    $variant={variant ?? 'default'}
  />
);
